import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSiren} from "../SirenContext";
import {useAuth} from "../../../context/user";
import {usePermissions} from "../../../context/modulePermissions";
import {ContactPhoneOptIn} from "../typedefs";
import {PaginatedSelector, HeaderComponentProps} from "../../../components/paginated-selector/paginated-selector";
import {useGetContactPhoneOptIns} from "../hooks/useGetContactPhoneOptIns";
import {useOptInSend, useOptInMarkExempt, useOptInReset, useOptInSendBulk} from "../hooks/useOptInActions";
import {OptInStatusFilter} from "../components/OptInStatusFilter";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {parsePhoneNumberWithError} from "libphonenumber-js";
import {countries} from "../../../lib/countries";
import ModalV2 from "../../../components/portal/ModalV2";
import {ButtonV2} from "../../../components/ui/ButtonsV2/ButtonV2";
import {ErrorMessage} from "../ErrorMessage";

const statusLabels: Record<string, string> = {
    not_sent: "Not Sent",
    queued:   "Queued",
    sent:     "Sent",
    failed:   "Failed",
    exempt:   "Exempt",
    revoked:  "Revoked",
};

function FormattedPhone({e164}: {e164: string}) {
    const {flag, formatted} = useMemo(() => {
        try {
            const parsed = parsePhoneNumberWithError(e164);
            const country = countries.find(c => c.countryCode === parsed.country);
            return {flag: country?.flag ?? "", formatted: parsed.formatInternational()};
        } catch {
            return {flag: "", formatted: e164};
        }
    }, [e164]);

    return <span>{flag} {formatted}</span>;
}

function OptInActions({item, refetch, isSuperAdmin, canWrite}: {item: ContactPhoneOptIn; refetch: () => void; isSuperAdmin: boolean; canWrite: boolean}) {
    const {send} = useOptInSend(item.id, refetch);
    const {markExempt} = useOptInMarkExempt(item.id, refetch);
    const {reset} = useOptInReset(item.id, refetch);

    const canSend = item.status === "not_sent" || item.status === "failed";
    const canMarkExempt = item.status !== "sent" && item.status !== "exempt";
    const canReset = item.status !== "not_sent";

    return (
        <ActionSelect label="Actions">
            {canWrite && canSend && <Action label="Send Opt-in" onSelect={send}/>}
            {isSuperAdmin && canMarkExempt && <Action label="Mark Exempt" onSelect={markExempt}/>}
            {isSuperAdmin && canReset && <Action label="Reset to Not Sent" onSelect={reset}/>}
        </ActionSelect>
    );
}

function SendBulkOptInsModal({clientID, onSuccess}: {clientID: string | null; onSuccess: () => void}) {
    const [open, setOpen] = useState(false);
    const [includeFailed, setIncludeFailed] = useState(false);
    const {sendBulk, loading, error} = useOptInSendBulk(clientID, () => { setOpen(false); onSuccess(); });

    return (
        <>
            <ButtonV2 label="Send Bulk Opt-ins" color="blue" onClick={() => setOpen(true)}/>
            <ModalV2 open={open} setOpen={setOpen}>
                <div className="flex flex-col gap-4 p-2">
                    <h2 className="text-lg font-semibold">Send Bulk Opt-ins</h2>
                    <p className="text-sm">This will send opt-in messages to all contacts with status <strong>Not Sent</strong>.</p>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeFailed}
                            onChange={e => setIncludeFailed(e.target.checked)}
                        />
                        Also send to contacts with status <strong>Failed</strong>
                    </label>
                    <ErrorMessage text={error}/>
                    <div className="flex gap-2 justify-end">
                        <ButtonV2 label="Cancel" color="gray" onClick={() => setOpen(false)}/>
                        <ButtonV2 label="Send" color="green" loading={loading} onClick={() => sendBulk(includeFailed)}/>
                    </div>
                </div>
            </ModalV2>
        </>
    );
}

export function ContactPhoneOptInList() {
    const {clientID} = useSiren();
    const {isSuperAdmin} = useAuth();
    const {hasFullAccess} = usePermissions();
    const canWrite = hasFullAccess("contacts");
    const getItems = useGetContactPhoneOptIns();
    const refetchRef = useRef<(() => void) | null>(null);

    const getColumns = useCallback((item: ContactPhoneOptIn) => {
        return [
            item.contactName,
            <FormattedPhone e164={item.e164}/>,
            statusLabels[item.status] ?? item.status,
            item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "",
        ];
    }, []);

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        if (props.columnIndex === 2) {
            return <OptInStatusFilter {...props}/>;
        }
        return <span className="font-bold">{props.label}</span>;
    }, []);

    useEffect(() => {
        const interval = setInterval(() => { refetchRef.current?.(); }, 10000);
        return () => clearInterval(interval);
    }, []);

    const renderActions = useCallback((item: ContactPhoneOptIn) => (
        <OptInActions item={item} refetch={() => refetchRef.current?.()} isSuperAdmin={isSuperAdmin} canWrite={canWrite}/>
    ), [isSuperAdmin, canWrite]);

    if (!clientID && isSuperAdmin) return null;

    return (
        <PaginatedSelector
            key={clientID}
            headers={["Contact", "Phone", "Status", "Created"]}
            columnTemplate="minmax(0,1fr) minmax(0,1fr) 160px 120px"
            getItems={getItems}
            getColumns={getColumns}
            disableSelect={true}
            renderHeader={renderHeader}
            renderActions={renderActions}
            nonClickableColumns={[0, 1, 2, 3]}
            syncWithUrl={true}
            onRefetchReady={(refetch) => { refetchRef.current = refetch; }}
            renderTopBarActions={canWrite ? () => (
                <div className="shrink-0">
                    <SendBulkOptInsModal clientID={clientID} onSuccess={() => refetchRef.current?.()}/>
                </div>
            ) : undefined}
        />
    );
}
